import React, { useMemo } from 'react';
import { type AxePurchaseState, type v2 } from '@deque/billing-service-client';
import { useTranslation, Trans } from 'react-i18next';

import {
  FREE_STATES,
  DOWNGRADABLE_STATES,
  ProductSlugs
} from '../../common/constants';
import ContentToast from '../../common/components/ContentToast';
import styles from './PlanView.css';
import ProductCard from '../components/ProductCard';
import { useAxeDevtoolsProAnalytics } from '../../common/contexts/analytics';
import useMarketingSearchParams from '../../common/hooks/useMarketingSearchParams';
import mergeSearchParams from '../../common/utils/merge-search-params';
import { useServerInfo } from '../../common/contexts/serverInfo';
import Link from '../../common/components/Link';

const PriceAfterTrial = ({
  price,
  term
}: {
  price: string;
  term: string;
}): React.ReactElement => {
  return (
    <Trans>
      <span>
        (PRICE AFTER TRIAL{' '}
        <strong>
          ${{ price }} PER USER/{{ term: term.toLocaleUpperCase() }}
        </strong>
        )
      </span>
    </Trans>
  );
};

const UpgradePrice = ({
  price,
  term
}: {
  price: string;
  term: string;
}): React.ReactElement => {
  return (
    <Trans>
      <span>
        AT{' '}
        <strong>
          ${{ price }}/{{ term: term.toLocaleUpperCase() }}
        </strong>
      </span>
    </Trans>
  );
};

const YourPrice = ({
  price,
  term
}: {
  price: string;
  term: string;
}): React.ReactElement => {
  return (
    <Trans>
      <span>
        YOU&apos;RE PAYING{' '}
        <strong>
          ${{ price }} per user,{' '}
          {{ term: term === 'month' ? 'monthly' : 'annually' }}
        </strong>
      </span>
    </Trans>
  );
};

interface PlanViewProps {
  state?: AxePurchaseState;
  canUserRestartTrial?: boolean;
  isAxeProOnline: boolean;
  enterpriseNameForNonAdmin?: string;
  statusMessage?: string;
  filteredProducts?: v2.Product[];
  priceAmount: string;
  term: string;
  handleDowngradeClick: () => void;
  trialDuration?: number;
  bundlePurchaseState: AxePurchaseState;
  isPendingCancellation?: boolean;
  statusVariant?: 'success' | 'warn' | 'alert';
}

const PlanView = ({
  state,
  canUserRestartTrial,
  isAxeProOnline,
  enterpriseNameForNonAdmin,
  statusMessage,
  filteredProducts,
  priceAmount,
  term,
  handleDowngradeClick,
  trialDuration,
  bundlePurchaseState,
  isPendingCancellation,
  statusVariant
}: PlanViewProps): React.ReactElement => {
  const { t } = useTranslation();
  const marketingSearchParams = useMarketingSearchParams();
  const { serverInfo } = useServerInfo();
  const isSelfProvisioningEnabled = serverInfo?.isSelfProvisioningEnabled;

  const purchaseSearchParams = mergeSearchParams(
    marketingSearchParams,
    `product=${ProductSlugs.axeDevToolsExtension}`
  );
  const purchaseUrl = `/purchase?${purchaseSearchParams}`;
  const contactSalesAction = 'https://www.deque.com/company/contact/';

  let expiredMessage: string | undefined, payMessage: string | undefined;
  const analytics = useAxeDevtoolsProAnalytics();
  switch (state) {
    case 'trial_ended':
      expiredMessage = t('Your trial has expired.');
      if (isSelfProvisioningEnabled) {
        payMessage = t('Upgrade Now');
      } else {
        payMessage = t('Contact our sales experts');
      }
      break;
    case 'paid_payment_failed':
      expiredMessage = t('Your renewal payment failed.');
      payMessage = t('Pay Now');
      break;
    case 'trial_payment_failed':
    case 'free_payment_failed':
      expiredMessage = t('Your upgrade payment failed.');
      payMessage = t('Pay Now');
      break;
  }

  const proCardActionButtonType = useMemo(() => {
    if (!isSelfProvisioningEnabled) {
      if (state === 'none') {
        return 'primary';
      }
      return 'secondary';
    }
    return 'primary';
  }, [serverInfo, state]);
  const proCardAction = useMemo(() => {
    if (!isSelfProvisioningEnabled) {
      if (state === 'none') {
        return `/trial?${marketingSearchParams}`;
      }
      return contactSalesAction;
    }
    if (state === 'paid') {
      return '/billing';
    } else if (state !== 'none') {
      return purchaseUrl;
    } else {
      return `/trial?${marketingSearchParams}`;
    }
  }, [serverInfo, state, marketingSearchParams]);
  const proCardActionText = useMemo(() => {
    if (!isSelfProvisioningEnabled) {
      if (state === 'none') {
        return t('Start Your Free Trial');
      }
      return t('Contact our sales experts');
    }
    if (state === 'paid') {
      return t('Manage billing');
    } else if (state === 'trialing' || state === 'free') {
      return t('Upgrade now');
    } else if (payMessage) {
      return payMessage;
    } else if (trialDuration) {
      return t('Start Your {{ trialDuration }}-day Free Trial', {
        trialDuration
      });
    } else {
      return t('Start Your Free Trial');
    }
  }, [serverInfo, state, trialDuration, payMessage]);

  // only online and trial_ended subscriptions are downgradable
  const downgradable =
    state &&
    DOWNGRADABLE_STATES.includes(state) &&
    !enterpriseNameForNonAdmin &&
    (isAxeProOnline || state === 'trial_ended') &&
    !isPendingCancellation &&
    serverInfo?.isSelfProvisioningEnabled;

  const showNoCreditCard = state === 'none';

  const ourToast = expiredMessage && payMessage && (
    <ContentToast type="caution" show>
      {expiredMessage}&nbsp;
      <Link
        className="Link"
        url={isSelfProvisioningEnabled ? purchaseUrl : contactSalesAction}
        onClick={() => {
          if (!isSelfProvisioningEnabled) {
            analytics.contactSalesExpertsClicked();
          }
        }}
      >
        {payMessage}
      </Link>
    </ContentToast>
  );

  const proPricePostAmble = useMemo(() => {
    if (!isSelfProvisioningEnabled) {
      return <span>&nbsp;</span>;
    }
    if (enterpriseNameForNonAdmin) {
      return <span>&nbsp;</span>;
    } else if (state === 'paid') {
      return <YourPrice price={priceAmount} term={term} />;
    } else if (state === 'trialing' || !!expiredMessage || state === 'free') {
      return <UpgradePrice price={priceAmount} term={term} />;
    } else {
      return (
        <>
          <PriceAfterTrial price={priceAmount} term={term} />
          <div className={styles.purchaseNow}>
            <Link url={purchaseUrl}>{t('OR PURCHASE PRO NOW')}</Link>
          </div>
        </>
      );
    }
  }, [
    serverInfo,
    enterpriseNameForNonAdmin,
    state,
    priceAmount,
    term,
    expiredMessage
  ]);

  React.useEffect(() => {
    analytics.viewPlans();
  }, []);

  // When the user implicitly has a Pro plan (via the HTML bundle), show Enterprise as active (or expired).
  // NOTE: only enterprises are eligible for the HTML bundle.
  let enterpriseStatusMessage: string | undefined = undefined;
  if (['paid', 'trialing'].includes(bundlePurchaseState)) {
    enterpriseStatusMessage = t('Active');
  } else if (bundlePurchaseState === 'trial_ended') {
    enterpriseStatusMessage = t('Trial Expired');
  } else if (FREE_STATES.includes(bundlePurchaseState)) {
    enterpriseStatusMessage = t('Subscription Cancelled');
  }

  let subAction = undefined;
  let subActionText = undefined;
  if (canUserRestartTrial) {
    subAction = '/trial';
    subActionText = t('REACTIVATE TRIAL');
  }

  return (
    <div>
      {!filteredProducts?.length ? (
        <ContentToast type="caution" show>
          {t('There are no valid products to display') as string}
        </ContentToast>
      ) : (
        <>
          {ourToast}
          <div className={styles.headContainer}>
            <h1>
              {state && state !== 'none'
                ? t('Manage Your Plan')
                : t('Try axe DevTools for FREE')}
            </h1>
            {state && state !== 'none' ? (
              <Trans>
                <p className={styles.mainMessage}>
                  Use the options below to upgrade, downgrade, or manage your
                  axe DevTools plan. You can also compare plans or{' '}
                  <a href="https://www.deque.com/company/contact/">
                    contact Deque
                  </a>{' '}
                  for help.
                </p>
              </Trans>
            ) : (
              <Trans>
                <p className={styles.mainMessage}>
                  Our fast, automated in-browser tests find 57% of issues while
                  you code. Our Intelligent Guided Testing™ can take you to 80%
                  and more. With zero false results, you’ll <em>know</em> the
                  issues you see are real.
                </p>
              </Trans>
            )}
          </div>
          <div className={styles.productContainer}>
            {state && state !== 'none' && state !== 'trialing' ? (
              <ProductCard
                productName={t('Free')}
                productMessage={t(
                  'Catch up to 50% of issues with automated testing'
                )}
                statusMessage={state !== 'paid' ? t('Active') : undefined}
                featureList={[t('Automated testing')]}
                buttonType={'secondary'}
                action={downgradable ? handleDowngradeClick : ''}
                actionText={t('Downgrade')}
                smallPrintText={t('Terms and conditions apply')}
                buttonDisable={!downgradable}
                variant="long"
              />
            ) : (
              ''
            )}
            <ProductCard
              productName={t('Pro')}
              productMessage={t('Efficient Testing without the rework')}
              statusMessage={statusMessage}
              statusVariant={statusVariant}
              featureList={[
                t('Lightweight browser extension'),
                t('Component-level testing'),
                t('Intelligent Guided Testing with machine learning'),
                t('Export, save, and share issues'),
                t('Detailed remediation help'),
                t('Rules and standards control')
              ]}
              buttonType={proCardActionButtonType}
              onClick={() => {
                if (state === 'paid') {
                  analytics.manageBillingClick();
                } else if (state === 'none') {
                  analytics.trialStartClick();
                }
              }}
              action={proCardAction}
              actionText={proCardActionText}
              smallPrintText={
                state === 'paid' ? (
                  !serverInfo?.isSelfProvisioningEnabled ? (
                    t('Terms and conditions apply')
                  ) : (
                    t(
                      'Click “Manage Billing” to change or update your payment method'
                    )
                  )
                ) : (
                  <Trans>
                    <a
                      href="https://www.deque.com/terms-of-use/axe-devtools-online/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms and conditions
                    </a>{' '}
                    apply. Purchase can be made any time after starting a trial.
                  </Trans>
                )
              }
              pricePreamble={
                !showNoCreditCard ? '' : t('NO CREDIT CARD REQUIRED!')
              }
              pricePostamble={proPricePostAmble}
              decorationStyle="redblue"
              buttonDisable={!!enterpriseNameForNonAdmin}
              variant="long"
              subAction={subAction}
              subActionText={subActionText}
            />
            <ProductCard
              productName={t('Enterprise')}
              productMessage={t('Comprehensive testing at scale')}
              compareProduct={t('Pro')}
              featureList={[
                t('CI/CD integration'),
                t('Command line interface (CLI)'),
                t('iOS and Android mobile testing'),
                t('Custom rules support'),
                t('Multi-level reporting')
              ]}
              buttonType={'secondary'}
              onClick={() => analytics.contactSalesExpertsClicked()}
              action={contactSalesAction}
              actionText={t('Contact our sales experts')}
              smallPrintText={t('Terms and conditions apply')}
              decorationStyle="greenyellow"
              variant="long"
              buttonDisable={false}
              statusMessage={enterpriseStatusMessage}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PlanView;
